/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Calepinage\CalepinageVisualisation.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
 * Ce composant visualise le calepinage d'un groupe de matières.
 * Il affiche les pièces placées sur la laize de matière, avec leurs dimensions et informations.
 * Utilise les API backend pour générer les visuels et les exports.
 */

import React, { useState, useEffect } from 'react';
import { GroupeMatiere } from '../../types/dimensions';
import { Matiere } from '../../pages/Devis/ConfigurateurArticle/types';
import { apiFetch } from '../../services/httpClient';
import { matiereService } from '../../services/MatiereService';
import ImageModal from './ImageModal';
import './Calepinage.css';

interface CalepinageVisualisationProps {
  groupe: GroupeMatiere;
  devisId: string;
  zoom?: number;
  onZoomChange?: (newZoom: number) => void;
}

const CalepinageVisualisation: React.FC<CalepinageVisualisationProps> = ({ 
  groupe, 
  devisId, 
  zoom = 1,
  onZoomChange 
}) => {
  const [exports, setExports] = useState<{
    preview: string | null;
    image: string | null;
    excel: string | null;
    csv: string | null;
  }>({ preview: null, image: null, excel: null, csv: null });
  const [comparaison, setComparaison] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingComparaison, setIsLoadingComparaison] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [hdImage, setHdImage] = useState<string | null>(null);
  const [isLoadingHd, setIsLoadingHd] = useState<boolean>(false);
  const [matieres, setMatieres] = useState<Matiere[]>([]);
  
  // Récupérer les informations du calepinage
  const longueurTotale = groupe.calepinage?.longueurTotale || 0;
  const tauxChute = groupe.calepinage?.tauxChute || 0;
  const { laize } = groupe;
  
  
  // Générer une image localement
  const generateLocalImage = () => {
    if (!groupe || !groupe.matiere || !groupe.couleur || !groupe.calepinage || !groupe.pieces) {
      return null;
    }
    
    try {
      // Créer un canvas
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return null;
      
      // Remplir le canvas avec une couleur de fond
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Dessiner un cadre pour la laize
      const padding = 40;
      const laize = groupe.laize || 100;
      
      // CORRECTION : Calculer la longueur totale réelle à partir des dimensions des pièces
      let longueurTotaleReelle = 0;
      if (groupe.pieces && groupe.pieces.length > 0) {
        // Pour les pièces grille, utiliser la dimension réelle de la pièce
        const pieceGrille = groupe.pieces.find(p => p.type === 'fond' && p.id.includes('mesh'));
        if (pieceGrille) {
          // Utiliser la hauteur de la pièce grille qui correspond à la longueur sur la laize
          longueurTotaleReelle = pieceGrille.height || pieceGrille.dimensions?.longueur || 100;
        } else {
          // Fallback : utiliser la plus grande dimension des pièces
          longueurTotaleReelle = Math.max(...groupe.pieces.map(p => 
            Math.max(p.height || 0, p.width || 0, p.dimensions?.longueur || 0, p.dimensions?.largeur || 0)
          ));
        }
      }
      
      // Si toujours pas de longueur, utiliser celle du calepinage
      const longueurTotale = longueurTotaleReelle || groupe.calepinage.longueurTotale || 100;
      
      // Calculer l'échelle
      const maxWidth = canvas.width - 2 * padding;
      const maxHeight = canvas.height - 2 * padding;
      
      const scaleX = maxWidth / laize;
      const scaleY = maxHeight / longueurTotale;
      const scale = Math.min(scaleX, scaleY);
      
      // Dessiner le cadre de la laize
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(
        padding, 
        padding, 
        laize * scale, 
        longueurTotale * scale
      );
      
      // Dessiner une grille tous les 10 cm
      ctx.strokeStyle = '#f0f0f0';
      ctx.lineWidth = 1;
      
      // Lignes horizontales
      for (let y = 0; y <= longueurTotale; y += 10) {
        ctx.beginPath();
        ctx.moveTo(padding, padding + y * scale);
        ctx.lineTo(padding + laize * scale, padding + y * scale);
        ctx.stroke();
      }
      
      // Lignes verticales
      for (let x = 0; x <= laize; x += 10) {
        ctx.beginPath();
        ctx.moveTo(padding + x * scale, padding);
        ctx.lineTo(padding + x * scale, padding + longueurTotale * scale);
        ctx.stroke();
      }
      
      // Dessiner les pièces
      if (groupe.calepinage.placement) {
        groupe.calepinage.placement.forEach(placement => {
          // Trouver la pièce correspondante
          const piece = groupe.pieces.find(p => p.id === placement.pieceId);
          if (!piece) return;
          
          // Déterminer la couleur en fonction du type de pièce
          let couleur = '#cccccc';
          switch (piece.type) {
            case 'dessus':
              couleur = '#4299e1'; // Bleu
              break;
            case 'fond':
              couleur = '#48bb78'; // Vert
              break;
            case 'bandeau':
              couleur = '#ed8936'; // Orange
              break;
            case 'joue1':
            case 'joue2':
              couleur = '#9f7aea'; // Violet
              break;
            case 'cylindre':
              couleur = '#f56565'; // Rouge
              break;
          }
          
          // Dessiner le rectangle
          ctx.fillStyle = couleur;
          ctx.fillRect(
            padding + placement.x * scale,
            padding + placement.y * scale,
            placement.width * scale,
            placement.height * scale
          );
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 0.5;
          ctx.strokeRect(
            padding + placement.x * scale,
            padding + placement.y * scale,
            placement.width * scale,
            placement.height * scale
          );
          
          // Ajouter un label avec le nom de la pièce
          const pieceLabel = piece.type.charAt(0).toUpperCase() + piece.type.slice(1);
          
          // Ajuster la taille de la police en fonction de la taille de la pièce
          const fontSize = Math.min(
            Math.max(8, Math.min(placement.width, placement.height) * scale / 8),
            14
          );
          
          ctx.font = `${fontSize}px Arial`;
          ctx.fillStyle = '#000000';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Calculer la position du texte
          const textX = padding + placement.x * scale + placement.width * scale / 2;
          const textY = padding + placement.y * scale + placement.height * scale / 2;
          
          // Dessiner le texte
          ctx.fillText(
            pieceLabel,
            textX,
            textY
          );
        });
      }
      
      // Ajouter un titre amélioré avec toutes les informations
      ctx.font = '16px Arial';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      
      // Calculer le nombre de pièces
      const nombrePieces = groupe.pieces ? groupe.pieces.length : 0;
      
      // Construire le titre avec nom matière, couleur, longueur, taux de chute et nombre de pièces
      const titre = `Calepinage - Matière ${groupe.matiere} ${groupe.couleur} - Longueur: ${longueurTotale.toFixed(1)} cm - Chute: ${(tauxChute * 100).toFixed(1)}% - ${nombrePieces} pièce(s)`;
      
      ctx.fillText(titre, canvas.width / 2, 20);
      
      // Convertir le canvas en base64
      const dataURL = canvas.toDataURL('image/png');
      return dataURL;
    } catch (error) {
      console.error("Erreur lors de la génération de l'image locale:", error);
      return null;
    }
  };

  // Fonction pour ouvrir l'image en plein écran
  const openFullScreen = async (typeCalepinage?: 'compact' | 'aligne') => {
    if (!groupe || !groupe.matiere || !groupe.couleur) return;
    
    setIsLoadingHd(true);
    setShowModal(true);
    
    const groupeId = `${groupe.matiere}|||${groupe.couleur}`;
    
    try {
      // Utiliser la bonne vignette selon le type de calepinage
      if (typeCalepinage === 'compact') {
        // Charger l'image HD du calepinage compact
        const data = await apiFetch<{image: string}>(`/calepinage/${groupeId}/export/compact?devis_id=${devisId}`);
        setHdImage(data.image);
      } else if (typeCalepinage === 'aligne') {
        // Charger l'image HD du calepinage aligné
        const data = await apiFetch<{image: string}>(`/calepinage/${groupeId}/export/aligne?devis_id=${devisId}`);
        setHdImage(data.image);
      } else if (exports.image) {
        // Fallback vers l'image du calepinage principal
        setHdImage(exports.image);
      } else {
        // Générer une image locale si pas d'image disponible
        const localImage = generateLocalImage();
        if (localImage) {
          const base64Image = localImage.split(',')[1];
          setHdImage(base64Image);
        }
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'image HD:", error);
      setHdImage(null);
    } finally {
      setIsLoadingHd(false);
    }
    }; 

  // Charger les matières
  useEffect(() => {
    const fetchMatieres = async () => {
      try {
        const matieresData = await matiereService.getMatieres();
        setMatieres(matieresData);
      } catch (error) {
        console.error("Erreur lors du chargement des matières:", error);
      }
    };
    
    fetchMatieres();
  }, []);

  // Fonctionnalité de comparaison supprimée (abandonnée)
  useEffect(() => {
    setIsLoadingComparaison(false);
    setComparaison(null);
  }, [groupe, devisId]);

  // Charger les exports au chargement du composant
  useEffect(() => {
    if (!groupe || !groupe.matiere || !groupe.couleur) return;
    
    setIsLoading(true);
    
    const groupeId = `${groupe.matiere}|||${groupe.couleur}`;
    
    
    // Essayer de charger les exports depuis le backend
    if (devisId) {
      apiFetch<any>(`/calepinage/${groupeId}/exports?devis_id=${devisId}`)
        .then(data => {
          // PRIORITÉ AU BACKEND : Toujours utiliser les données du backend si disponibles
          if (data && Object.keys(data).length > 0) {
            setExports(data);
          }
        })
        .catch(err => {
          console.error("Erreur lors du chargement des exports depuis le backend:", err);
          // Nous continuons à utiliser l'image générée localement
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [groupe, devisId]);
  
  // Si le groupe n'a pas de calepinage, ne rien afficher
  if (!groupe.calepinage || groupe.pieces.length === 0) {
    return (
      <div className="text-center p-4">
        <p className="text-gray-500">Aucun calepinage disponible pour ce groupe.</p>
      </div>
    );
  }
  
  return (
    <div className="calepinage-container">
      {/* Affichage du calepinage classique */}
      <div className="calepinage-visualisation border border-gray-300 overflow-auto relative">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : exports.preview ? (

          <div className="relative group">
            <img 
              src={`data:image/png;base64,${exports.preview}`} 
              alt="Aperçu du calepinage" 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              title="Cliquer pour agrandir"
              onClick={() => openFullScreen()}
              style={{ 
                transform: `scale(${zoom})`,
                transformOrigin: 'top left'
              }}
            />
            {/* Icône de loupe centrée sur l'image réelle */}
            <div 
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
              style={{
                top: 0,
                left: 0,
                transform: `translate(calc(50% * ${zoom}), calc(50% * ${zoom})) translate(-50%, -50%)`,
                transformOrigin: 'top left',
                zIndex: 10
              }}
            >
              <div className="bg-white bg-opacity-90 rounded-full p-3 shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <p className="text-gray-500">Aucun aperçu disponible</p>
          </div>
        )}
      </div>

      {/* Modal plein écran */}
      <ImageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        imageBase64={hdImage}
        groupeId={`${groupe.matiere}|||${groupe.couleur}`}
        matiereNom={matieres.find(m => m.id === groupe.matiere)?.nom || groupe.matiere}
        couleur={groupe.couleur}
        isLoading={isLoadingHd}
      />
    </div>
  );
};

export default CalepinageVisualisation;
